/**
 * @fileoverview Strict, timezone-free calendar-date parsing (MOR-2342).
 *
 * `new Date("05/09/2021")` is parsed as US month-first and then read back
 * through local or UTC getters, so an Australian day-first value can shift
 * by a month and, near midnight, by a day. Every place that turns a typed
 * date value into digits or DD/MM/YYYY text must go through this parser
 * instead: it accepts only a complete day-first date (`/`, `.` or `-`) or an
 * ISO date, validates the real calendar (leap years included) with pure
 * arithmetic, and never consults the runtime timezone.
 *
 * Used by: convex/jobs/precedents/fillPdfCore (fill + boxed groups) and, via
 * that module, the PDF field editor's preview stream.
 */

export interface CalendarDate {
  y: number;
  m: number;
  d: number;
}

const ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const DAY_FIRST_RE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

/** Days in `m` (1–12) of year `y`, leap-aware. */
export function daysInMonth(y: number, m: number): number {
  if (m === 2) {
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

/**
 * Parse a complete date, day-first (`5/9/2021`, `05.09.2021`, `05-09-2021`)
 * or ISO (`2021-09-05`). Returns null for anything else, including a
 * date-like value that is not a real calendar day (`31/02/2024`,
 * `12/34/5678`) and a partial or two-digit-year value (`1/2/34`).
 *
 * @example
 * parseCalendarDate("05/09/2021"); // { y: 2021, m: 9, d: 5 }
 * parseCalendarDate("2024-02-29"); // { y: 2024, m: 2, d: 29 }
 * parseCalendarDate("31/02/2024"); // null
 */
export function parseCalendarDate(value: string): CalendarDate | null {
  const trimmed = value.trim();
  const iso = ISO_RE.exec(trimmed);
  const dayFirst = iso ? null : DAY_FIRST_RE.exec(trimmed);
  const parts = iso
    ? { y: Number(iso[1]), m: Number(iso[2]), d: Number(iso[3]) }
    : dayFirst
      ? {
          y: Number(dayFirst[3]),
          m: Number(dayFirst[2]),
          d: Number(dayFirst[1]),
        }
      : null;
  if (!parts) return null;
  if (parts.m < 1 || parts.m > 12) return null;
  if (parts.d < 1 || parts.d > daysInMonth(parts.y, parts.m)) return null;
  return parts;
}

/**
 * True when the value has the SHAPE of a calendar date (day-first or ISO),
 * whether or not it is a real day. A calendar-shaped value must never fall
 * through to a permissive `Date` parser, which would silently normalise
 * "2024-02-31" into 2 March.
 *
 * @example
 * isCalendarShaped("2024-02-31"); // true (shape only)
 * isCalendarShaped("2026-08-26T23:30:00Z"); // false (a timestamp)
 */
export function isCalendarShaped(value: string): boolean {
  const trimmed = value.trim();
  return ISO_RE.test(trimmed) || DAY_FIRST_RE.test(trimmed);
}

/**
 * An explicit ISO 8601 timestamp: `YYYY-MM-DDTHH:MM[:SS[.fff]]` followed by
 * `Z` or a `±HH:MM` offset (a missing designator is read as UTC). Nothing
 * else is a timestamp here — the runtime `Date` parser is never consulted
 * for anything that does not match this shape.
 */
const ISO_TIMESTAMP_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * The UTC calendar date of an explicit ISO 8601 timestamp, or null for any
 * other string — including a timestamp whose date component is not a real
 * day. Pure arithmetic: the offset is applied to `Date.UTC`, so the result is
 * identical in every runtime timezone.
 *
 * @example
 * parseIsoTimestampUtcDate("2026-08-26T23:30:00Z"); // { y: 2026, m: 8, d: 26 }
 * parseIsoTimestampUtcDate("2026-08-26T23:30:00+10:00"); // { y: 2026, m: 8, d: 26 } (13:30Z)
 * parseIsoTimestampUtcDate("2024-02-31T10:00:00Z"); // null
 * parseIsoTimestampUtcDate("A/B 12"); // null
 */
export function parseIsoTimestampUtcDate(value: string): CalendarDate | null {
  const match = ISO_TIMESTAMP_RE.exec(value.trim());
  if (!match) return null;
  const date = parseCalendarDate(`${match[1]}-${match[2]}-${match[3]}`);
  if (!date) return null;
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] ?? "0");
  if (hour > 23 || minute > 59 || second > 59) return null;
  const designator = match[7] ?? "Z";
  const offsetMinutes =
    designator === "Z"
      ? 0
      : (designator.startsWith("-") ? -1 : 1) *
        (Number(designator.slice(1, 3)) * 60 + Number(designator.slice(4, 6)));
  const utcMs =
    Date.UTC(date.y, date.m - 1, date.d, hour, minute, second) -
    offsetMinutes * 60_000;
  const utc = new Date(utcMs);
  return {
    y: utc.getUTCFullYear(),
    m: utc.getUTCMonth() + 1,
    d: utc.getUTCDate(),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** `DD/MM/YYYY`. */
export function formatDayFirst(date: CalendarDate): string {
  return `${pad2(date.d)}/${pad2(date.m)}/${date.y}`;
}

/** The eight digits `DDMMYYYY`. */
export function calendarDateDigits(date: CalendarDate): string {
  return `${pad2(date.d)}${pad2(date.m)}${date.y}`;
}
